import React, { useCallback, useRef } from 'react';
import { message, Table, TablePaginationConfig } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ShopCategoriesType } from '../../../types/shopCategories';
import { SHOP_CATEGORIES } from '../../../constants/define/translation';
import { ActionsColumn, ShowHideColumn, TitleColumn, LinkColumn } from './columns';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { postSortShopCategoriesRepository } from '../../../repository/mytm/shop-categories/shopCategoriesRepository';
import { IndexColumnSort } from '../shared-column';

interface ShopCategoriesTableProps {
  records: ShopCategoriesType[];
  onRefresh: () => void;
  handleChangeInputLink: Function;
  removeItemByIndex: Function;
  handleAddLink: Function;
  handleChangePageActive: Function;
  loading?: boolean;
  setLoading: (value: boolean) => void;
  className?: string;
  isDraft?: boolean;
  pagination?: TablePaginationConfig;
  handleChangeInputTitle: Function;
  handleChangeEditMode: Function;
  onFocusNewRow: Function;
}

const DraggableBodyRow = ({ index, moveRow, className, style, record, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !!record.id,
  });
  drop(drag(ref));
  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

const { Column } = Table;

export const ShopCategoriesTable = (props: ShopCategoriesTableProps) => {
  const { t } = useTranslation(SHOP_CATEGORIES);
  const {
    pagination,
    records,
    loading,
    setLoading,
    className,
    handleChangeInputLink,
    handleAddLink,
    onRefresh,
    handleChangeInputTitle,
    onFocusNewRow,
  } = props;

  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      // setLoading(true);
      let dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);
      dragRecordsList = dragRecordsList.filter(val => val.id);

      // eslint-disable-next-line consistent-return
      const sortList = dragRecordsList.map((record, index) => ({
        id: record.id,
        sortOrder: index + 1,
      }));

      postSortShopCategoriesRepository({
        sortOrderList: sortList,
      })
        .then(() => {
          setLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setLoading(false);
        });
    },
    [records, onRefresh],
  );

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledTable
          dataSource={records}
          loading={loading}
          className={className}
          rowKey="index"
          pagination={pagination}
          components={components}
          summary={() => {
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={2} index={0}>
                    <span className="summary-text"> Total: {pagination?.total}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
          onRow={(record: any, index?: number): any => {
            return {
              index,
              moveRow,
              record,
            };
          }}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            width="5%"
            dataIndex="index"
            key="index"
            render={value => <IndexColumnSort value={value} />}
          />
          <Column
            title={t('table.title.type')}
            align="center"
            dataIndex="show"
            key="show"
            width="10%"
            render={(_, record: ShopCategoriesType) => <ShowHideColumn onRefresh={onRefresh} record={record} />}
          />
          <Column
            title={t('table.title.title')}
            align="left"
            width="20%"
            key="title"
            render={(_, record: ShopCategoriesType) => (
              <TitleColumn
                record={record}
                handleChangeInputTitle={handleChangeInputTitle}
                onFocusNewRow={onFocusNewRow}
              />
            )}
          />
          <Column
            title={t('table.title.url')}
            align="left"
            width="70%"
            key="url"
            render={(_, record: ShopCategoriesType) => (
              <LinkColumn
                record={record}
                handleChangeInputLink={handleChangeInputLink}
                handleAddLink={handleAddLink}
                loading={loading}
                onFocusNewRow={onFocusNewRow}
              />
            )}
          />
          <Column
            title={t('table.title.action')}
            align="center"
            key="action"
            width="10%"
            render={(_, record: ShopCategoriesType) => (
              <ActionsColumn total={records.length} record={record} {...props} />
            )}
          />
        </StyledTable>
      </DndProvider>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .ant-table-summary > tr > td .summary-text {
    font-weight: 700;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }

  .ant-table-thead > tr > th {
    text-transform: none;
    font-weight: 400;
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    padding: 9px;
  }
`;
